// Styles
import '@mdi/font/css/materialdesignicons.css'
import 'vuetify/styles'
import { VTimePicker } from 'vuetify/labs/VTimePicker'
import { VDateInput } from 'vuetify/labs/VDateInput'
// Vuetify
import { createVuetify } from 'vuetify'

// INT Logistics & Manpower brand palette: black, yellow, white.
// Semantic colors are the single source of truth — components reference
// these names (success/info/warning/error/qa/neutral) instead of hex
// literals, so the whole app re-themes from one place.
const intLight = {
  dark: false,
  colors: {
    background: '#F4F5F7',
    surface: '#FFFFFF',
    'surface-bright': '#FFFFFF',
    'surface-variant': '#E9EAEE',
    'surface-muted': '#F7F8FA',      // subtle fills (table heads, hovers)
    'on-surface-variant': '#46474B',
    primary: '#FDCC0E',              // brand yellow (mirrors Claims CMS)
    'primary-darken-1': '#D9A400',
    secondary: '#121212',            // brand black
    'secondary-darken-1': '#000000',
    accent: '#FDCC0E',
    success: '#2E9E5B',              // done / pass / resolved
    info: '#2C7BE5',                 // in progress / informational
    warning: '#E8930C',              // blocked / overdue / pending
    error: '#E03B3B',                // fail / open / critical
    qa: '#7B61FF',                   // QA module accent (was hardcoded)
    neutral: '#6B6C72',              // muted neutral state (todo / closed)
    // Light "cream" sidebar mirrored from the Claims CMS (INT Portal look).
    sidebar: '#FBEFC2',
    'sidebar-soft': '#F6E4A8',
    'sidebar-active': '#1F6BFF',     // blue text/icon on the white active pill
  },
  variables: {
    'border-color': '#EDEEF1',
    'border-opacity': 1,
    'theme-on-primary': '#121212',
    // Elevation tokens used by the global CSS shadow utilities.
    'shadow-key-umbra-opacity': 0.05,
  },
}

// Dark counterpart. Brand yellow stays the accent; surfaces/text invert to a
// calm dark palette so the app reads well in the iPhone's dark appearance.
const intDark = {
  dark: true,
  colors: {
    background: '#0F1115',
    surface: '#1A1D23',
    'surface-bright': '#22262E',
    'surface-variant': '#2A2E37',
    'surface-muted': '#22262E',
    'on-surface-variant': '#C9CBD1',
    primary: '#FDCC0E',
    'primary-darken-1': '#D9A400',
    secondary: '#E9EAEE',            // light "black" so avatars stay visible
    'secondary-darken-1': '#FFFFFF',
    accent: '#FDCC0E',
    success: '#3AAE6A',
    info: '#4C8DEE',
    warning: '#E8930C',
    error: '#E55555',
    qa: '#9A83FF',
    neutral: '#9A9BA0',
    sidebar: '#15171C',
    'sidebar-soft': '#1F232B',
    'sidebar-active': '#4C8DEE',
  },
  variables: {
    'border-color': '#2A2E37',
    'border-opacity': 1,
    'theme-on-primary': '#121212',
    'shadow-key-umbra-opacity': 0.4,
  },
}

export default createVuetify({
  components: {
    VTimePicker,
    VDateInput,
  },
  theme: {
    defaultTheme: 'intLight',
    themes: { intLight, intDark },
  },
  defaults: {
    global: {
      // Soft, consistent rounding everywhere.
    },
    VCard: { rounded: 'lg', elevation: 0, border: true },
    VBtn: { rounded: 'lg', flat: true },
    VChip: { rounded: 'lg' },
    VTextField: { variant: 'outlined', color: 'primary', density: 'comfortable', rounded: 'md' },
    VTextarea: { variant: 'outlined', color: 'primary', density: 'comfortable', rounded: 'md' },
    VAutocomplete: { variant: 'outlined', color: 'primary', density: 'comfortable', rounded: 'md' },
    VSelect: { variant: 'outlined', color: 'primary', density: 'comfortable', rounded: 'md' },
    VFileInput: { variant: 'outlined', color: 'primary', density: 'comfortable' },
    VToolbar: { color: 'surface' },
    // Consistent, comfortable tables across every list view. Phone card layout
    // is handled by CSS in App.vue (see the max-width:600px block).
    VDataTable: { density: 'comfortable', hover: true },
    // Dialogs share one rounding + width rhythm.
    VDialog: { scrollable: true },
    VTooltip: { location: 'bottom' },
    VChipGroup: { color: 'primary' },
    VProgressLinear: { rounded: true },
    VAlert: { rounded: 'lg', border: 'start' },
  },
})
